import { useEffect, useEffectEvent, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AnimatePresence, motion } from 'motion/react';
import { orderBy } from 'lodash';
import { useAutomationAnalytics, useAutomations } from '../queries';
import { useAutomationActions } from '../hooks';
import { useAutomationsStore } from '../stores';
import { AutomationRow } from './AutomationRow';
import { AutomationDetails } from './AutomationDetails';
import { AUTOMATION_HEADER_COLUMNS } from '../constants';
import {
    StanAccordion,
    StanAccordionBody,
    StanAccordionHeader,
    StanAccordionItem,
    StanDataRowGroup,
} from '@/components';
import { useAccordionController } from '@/hooks/useAccordionController';
import { DeleteAutomationModal } from './DeleteAutomationModal';

export const AutomationsList = () => {
    const { data: automations = [], isLoading: isLoadingAutomations } = useAutomations();

    // Store state for unsaved automation
    const unsavedAutomation = useAutomationsStore(state => state.unsavedAutomation);
    const clearUnsavedAutomation = useAutomationsStore(state => state.clearUnsavedAutomation);
    const prevUnsavedRef = useRef<boolean>(false);

    // Fetch analytics for all automations
    const automationIds = useMemo(() => automations.map(a => a.automation_id), [automations]);
    const { data: analyticsData } = useAutomationAnalytics(automationIds);

    // Merge analytics data with automations, add unsavedAutomation, and sort by ID descending
    const automationsWithAnalytics = useMemo(() => {
        let merged = automations;
        if (analyticsData?.analytics) {
            merged = automations.map(automation => ({
                ...automation,
                analytics: {
                    ...automation.analytics,
                    ...(analyticsData.analytics[automation.automation_id.toString()] || {}),
                },
            }));
        }

        // Sort saved automations by automation_id descending (newest first)
        const sortedAutomations = orderBy(merged, ['automation_id'], ['desc']);

        // Add unsavedAutomation at the beginning if it exists
        return unsavedAutomation ? [unsavedAutomation, ...sortedAutomations] : sortedAutomations;
    }, [automations, analyticsData, unsavedAutomation]);

    const {
        handlePublishAutomation,
        handleUnpublishAutomation,
        openDeleteModal,
        closeDeleteModal,
        confirmDelete,
        deleteModalState,
    } = useAutomationActions();

    const handleRemoveUnsaved = () => {
        clearUnsavedAutomation();
    };

    const firstLoadDoneRef = useRef(false);
    const prevCountRef = useRef(0);
    const prevAutomationIdsRef = useRef<Set<number>>(new Set());
    const [searchParams] = useSearchParams();
    const openAutomationId = searchParams.get('open');

    const accordionController = useAccordionController({
        allowMultipleOpen: false,
        initialOpenItems: openAutomationId ? [`automation-${openAutomationId}`] : [],
    });

    // Open accordion when unsaved automation is added
    const openUnsavedAccordion = useEffectEvent(() => {
        setTimeout(() => {
            accordionController.openItem('automation-undefined');
        }, 450);
    });

    useEffect(() => {
        const hasUnsaved = unsavedAutomation !== null;
        const wasUnsaved = prevUnsavedRef.current;

        if (hasUnsaved && !wasUnsaved) {
            openUnsavedAccordion();
        }

        prevUnsavedRef.current = hasUnsaved;
    }, [unsavedAutomation]);

    useEffect(() => {
        if (!openAutomationId || isLoadingAutomations) return;

        const automationId = `automation-${openAutomationId}`;
        const automationExists = automations.some(
            automation => automation.automation_id.toString() === openAutomationId
        );

        if (automationExists) {
            requestAnimationFrame(() => {
                document.getElementById(automationId)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            });
        }
    }, [openAutomationId, isLoadingAutomations, automations]);

    const handleNewAutomation = useEffectEvent((automationId: string) => {
        clearUnsavedAutomation();
        accordionController.openItem(automationId);

        // Wait for the exit/enter animation to complete (400ms) plus accordion expansion
        setTimeout(() => {
            document.getElementById(automationId)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 600);
    });

    useEffect(() => {
        if (isLoadingAutomations) return;

        const curr = automations.length;
        const currentIds = new Set(automations.map(a => a.automation_id));

        if (!firstLoadDoneRef.current) {
            firstLoadDoneRef.current = true;
            prevCountRef.current = curr;
            prevAutomationIdsRef.current = currentIds;
            return;
        }

        const prev = prevCountRef.current;
        if (curr > prev) {
            // Find the newly created automation by comparing IDs
            const newAutomationId = automations.find(
                a => !prevAutomationIdsRef.current.has(a.automation_id)
            )?.automation_id;

            if (newAutomationId) {
                const automationId = `automation-${newAutomationId}`;
                handleNewAutomation(automationId);
            }
        }

        prevCountRef.current = curr;
        prevAutomationIdsRef.current = currentIds;
    }, [automations, isLoadingAutomations]);

    return (
        <>
            <StanDataRowGroup headerColumns={[...AUTOMATION_HEADER_COLUMNS]} maxActionButtonCount={1}>
                <StanAccordion controller={accordionController}>
                    <AnimatePresence initial={false}>
                        {automationsWithAnalytics.map((automation, index) => {
                            const isUnsaved = automation.automation_id === undefined;
                            const isFirst = index === 0;

                            return (
                                <motion.div
                                    key={automation.automation_id ?? 'new'}
                                    initial={{ opacity: 0, height: 0 }}
                                    animate={{
                                        opacity: 1,
                                        height: 'auto',
                                    }}
                                    exit={{ opacity: 0, height: 0 }}
                                    transition={{
                                        duration: 0.4,
                                        ease: 'backOut',
                                    }}
                                    className={!isFirst ? 'mt-4 md:mt-6' : ''}
                                >
                                    <StanAccordionItem
                                        id={`automation-${automation.automation_id ?? 'undefined'}`}
                                        isCard
                                        className='bg-bg-canvas shadow-stan!'
                                    >
                                        <StanAccordionHeader asChild>
                                            <AutomationRow
                                                automation={automation}
                                                isDeleting={false}
                                                onDelete={isUnsaved ? handleRemoveUnsaved : openDeleteModal}
                                                onPublish={handlePublishAutomation}
                                                onUnpublish={handleUnpublishAutomation}
                                            />
                                        </StanAccordionHeader>
                                        <StanAccordionBody>
                                            <AutomationDetails automation={automation} />
                                        </StanAccordionBody>
                                    </StanAccordionItem>
                                </motion.div>
                            );
                        })}
                    </AnimatePresence>
                </StanAccordion>
            </StanDataRowGroup>

            <DeleteAutomationModal
                isOpen={deleteModalState.isOpen}
                onConfirm={confirmDelete}
                onCancel={closeDeleteModal}
            />
        </>
    );
};
